import { useUser } from "../../classes/contexts/UserContext";
import IconsEnum from "../../classes/enums/IconsEnum";
import {
  IMAGE_ORIENTATION_ABBREVIATIONS,
  IMAGE_ORIENTATION_LABELS,
  MEDICAL_IMAGE_THUMBNAIL_DISPLAY_MODE,
  MODALITY_LABELS,
  TOMOGRAPHIC_MODALITIES,
  standardizeModality,
} from "../../classes/enums/MedicalImagesEnum";
import { iconMapping } from "../../config/iconConfig";
import { dbDateFormat, formatDisplayDate } from "../../classes/utils/DateUtils";
import { parseOrientationData } from "../../classes/utils/OrientationUtils";
import useTranslation from "../../hooks/i18n/useTranslation";
import { useDateFormatter } from "../../hooks/useDateFormatter";
import { useKeyboardActivation } from "../../hooks/useKeyboardActivation";
import Tooltip from "../tooltip/Tooltip";
import "./MedicalImageThumbnail.css";

const getOrientationAbbreviation = orientation => {
  if (!orientation) return "";
  const normalized = IMAGE_ORIENTATION_ABBREVIATIONS[orientation];
  if (normalized) {
    return normalized;
  }
  return orientation.charAt(0).toUpperCase();
};

const formatOrientationLabel = orientation => {
  if (!orientation) return "";
  const resolved = IMAGE_ORIENTATION_LABELS[orientation];
  if (resolved) {
    return resolved;
  }
  return orientation.charAt(0).toUpperCase() + orientation.slice(1);
};

const normalizeSliceInfo = sliceInfo => {
  if (!sliceInfo) return null;

  const current = Number(sliceInfo.current);
  const max = Number(sliceInfo.max);

  if (!Number.isFinite(current) || !Number.isFinite(max)) {
    return null;
  }

  return {
    current: Math.max(0, Math.round(current)),
    max: Math.max(0, Math.round(max)),
  };
};

export const MedicalImageThumbnail = ({
  instance,
  isSelected,
  onClick,
  displayMode = MEDICAL_IMAGE_THUMBNAIL_DISPLAY_MODE.DEFAULT,
  displayData = {},
}) => {
  const { dateSettings } = useUser();
  const safeInstance = instance || {};
  const formatDate = useDateFormatter();
  const { t } = useTranslation();
  // Get file name, remove extension, and clamp to first 5 letters
  const fullFileName = safeInstance.file_name || t("346");
  const fileNameWithoutExtension = fullFileName.replace(/\.[^/.]+$/, "");
  const shortFileName =
    fileNameWithoutExtension.length > 5
      ? `${fileNameWithoutExtension.substring(0, 5)}...`
      : fileNameWithoutExtension || t("346");

  const modalitySource = displayData.modality || safeInstance.modality || "";
  const standardizedModality = standardizeModality(modalitySource);
  const isTomographic =
    standardizedModality &&
    TOMOGRAPHIC_MODALITIES.includes(standardizedModality);
  const modalityLabel =
    MODALITY_LABELS[standardizedModality] || modalitySource || "";

  const orientationSource =
    displayData.orientation ||
    safeInstance.orientation ||
    safeInstance.viewType ||
    "";

  // Parse orientation using robust parser
  const parsedOrientation = parseOrientationData(orientationSource);
  const resolvedOrientation = parsedOrientation.plane || orientationSource;
  const formattedOrientation = formatOrientationLabel(resolvedOrientation);

  // Helper to normalize date to YYYYMMDD format before formatting
  const normalizeDate = dateValue => {
    if (!dateValue) return null;

    // If already in YYYYMMDD format (8 digits), use as-is
    if (typeof dateValue === "string" && /^\d{8}$/.test(dateValue)) {
      return dateValue;
    }

    // If it's an ISO string or Date object, convert to YYYYMMDD
    return dbDateFormat(dateValue);
  };

  // Format the date if available
  const dateValue = safeInstance.date || safeInstance.creation_date;
  const normalizedDate = normalizeDate(dateValue);
  const formattedDate = normalizedDate
    ? formatDisplayDate(normalizedDate, {
        locale: dateSettings?.locale,
        format: dateSettings?.format,
      })
    : "No date";

  const normalizedSliceInfo = isTomographic
    ? normalizeSliceInfo(displayData.sliceInfo)
    : null;

  const baseOverlayName = `${shortFileName}${
    resolvedOrientation
      ? `(${getOrientationAbbreviation(resolvedOrientation)})`
      : ""
  }`;
  const baseOverlaySecondary = formattedDate;

  const annexOverlayName =
    displayData.name ||
    [modalityLabel, formattedOrientation].filter(Boolean).join(" - ") ||
    baseOverlayName;

  const annexSecondary = normalizedSliceInfo
    ? `${t("393")} ${normalizedSliceInfo.current} ${t("394")} ${normalizedSliceInfo.max}`
    : formattedOrientation || "";
  const annexTertiary = formattedDate;

  const isAnnexDisplay =
    displayMode === MEDICAL_IMAGE_THUMBNAIL_DISPLAY_MODE.ANNEX;

  const overlayName = isAnnexDisplay ? annexOverlayName : baseOverlayName;
  const overlaySecondary = isAnnexDisplay
    ? annexSecondary
    : baseOverlaySecondary;
  const overlayTertiary = isAnnexDisplay ? annexTertiary : null;

  const thumbnailClassNames = [
    "medical-image-thumbnail",
    isAnnexDisplay ? "medical-image-thumbnail--annex" : "",
    isSelected ? "selected" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const FallbackIcon = iconMapping[IconsEnum.MEDICALIMAGE];
  const hasValidUrl = safeInstance.url && safeInstance.url.trim() !== "";

  return (
    <div
      className={thumbnailClassNames}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={useKeyboardActivation(onClick)}
    >
      <div className="medical-image-thumbnail-preview">
        {hasValidUrl ? (
          <img
            src={safeInstance.url}
            alt={shortFileName}
            className="medical-image-thumbnail-image"
          />
        ) : (
          <div className="medical-image-thumbnail-placeholder">
            <FallbackIcon className="medical-image-thumbnail-placeholder-icon" />
            <span className="medical-image-thumbnail-placeholder-label">
              {modalityLabel || standardizedModality}
            </span>
          </div>
        )}
        <div className="medical-image-thumbnail-overlay">
          <Tooltip text={fileNameWithoutExtension}>
            <div className="medical-image-thumbnail-overlay-name">
              {overlayName}
            </div>
          </Tooltip>
          {overlaySecondary && (
            <div className="medical-image-thumbnail-overlay-date">
              {overlaySecondary}
            </div>
          )}
          {overlayTertiary && (
            <div className="medical-image-thumbnail-overlay-extra">
              {overlayTertiary}
            </div>
          )}
        </div>
      </div>

      <div className="medical-image-thumbnail-info">
        <Tooltip text={fileNameWithoutExtension}>
          <div className="medical-image-thumbnail-modality">
            {shortFileName}
          </div>
        </Tooltip>
        <div className="medical-image-thumbnail-date">{formattedDate}</div>
      </div>

      {isSelected && (
        <div className="medical-image-thumbnail-marker">&#10003;</div>
      )}
    </div>
  );
};
